
import React, { useEffect, useState } from 'react';
import { User } from '../types';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import { ArrowLeft, ShieldCheck, Loader2, CreditCard, Calendar } from './Icons';

interface Props {
  user: User;
  onNavigate: (view: any) => void;
}

const SubscriptionManager: React.FC<Props> = ({ user, onNavigate }) => {
  const [sub, setSub] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
        const data = await subscriptionService.getSubscription();
        setSub(data);
        setLoading(false);
    };
    load();
  }, [user.id]);

  const handlePortal = async () => {
    try {
        const url = await subscriptionService.getPortalUrl();
        window.location.href = url;
    } catch (e) {
        alert("Could not open portal. Please try again later.");
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto py-10 px-4 animate-slide-up pb-20">
       <button 
        onClick={() => onNavigate('landing')}
        className="mb-8 flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </button>

      <div className="glass-panel p-8 md:p-10 rounded-[2.5rem] border border-white/5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-forge-500/10 rounded-full blur-[80px] -z-10"></div>
        
        <h1 className="text-3xl font-serif font-bold text-white mb-8">Your Subscription</h1>

        {loading ? (
            <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gold-400" />
            </div>
        ) : sub ? (
            <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-black/20 p-6 rounded-2xl border border-white/5">
                        <div className="flex items-center gap-3 mb-2 text-gray-400 text-xs font-bold uppercase tracking-wider">
                            <CreditCard className="w-4 h-4" /> Current Plan
                        </div>
                        <div className="text-2xl font-bold text-white capitalize">
                            {sub.plan} Plan
                        </div>
                        <div className="mt-2 inline-block px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full uppercase">
                            {sub.status}
                        </div>
                    </div>

                    <div className="bg-black/20 p-6 rounded-2xl border border-white/5">
                        <div className="flex items-center gap-3 mb-2 text-gray-400 text-xs font-bold uppercase tracking-wider">
                            <Calendar className="w-4 h-4" /> Renewal
                        </div>
                        <div className="text-xl font-mono text-white">
                            {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                        </div>
                        {sub.cancelAtPeriodEnd && (
                            <div className="mt-2 text-red-400 text-xs font-bold">
                                Cancels at end of period
                            </div>
                        )}
                    </div>
                </div>

                <div className="pt-6 border-t border-white/5">
                    <button 
                        onClick={handlePortal}
                        className="w-full py-4 bg-white text-black font-bold rounded-2xl hover:bg-gray-200 transition-colors shadow-lg"
                    >
                        Manage Billing & Cancel
                    </button>
                    <p className="text-center text-gray-500 text-xs mt-4">
                        You will be redirected to the secure customer portal.
                    </p>
                </div>
            </div>
        ) : (
            <div className="text-center py-10">
                <p className="text-gray-400 mb-6">You don't have an active subscription.</p>
                <button 
                    onClick={() => onNavigate('landing')}
                    className="px-8 py-3 btn-primary-gradient text-white font-bold rounded-xl"
                >
                    View Plans
                </button>
            </div>
        )}
      </div>

      <div className="mt-8 flex justify-center gap-2 text-gray-500 text-[10px] uppercase tracking-widest">
        <ShieldCheck className="w-4 h-4" />
        Secure Payments via Polar
      </div>
    </div>
  );
};

export default SubscriptionManager;
